// Datetime presentation helpers (F-E Phase 4; F-AC — the one place staff
// screens format dates, ADR-0030).
//
// Wire format is ISO-8601 UTC (conventions.md); the UI presents Hong Kong time
// (Asia/Hong_Kong) whatever the browser's own time zone or locale. No date
// library — `Intl.DateTimeFormat` + `formatToParts` gives a deterministic
// "YYYY-MM-DD HH:MM:SS" regardless of locale ordering. 24-hour clock; the
// "(HKT)" label goes on the column / field, not on each value.

const _parts = (iso: string, timeZone: string): Record<string, string> =>
  Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
      .formatToParts(new Date(iso))
      .map((p) => [p.type, p.value]),
  );

function _format(iso: string, timeZone: string): string {
  const p = _parts(iso, timeZone);
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`;
}

/** ISO-8601 UTC → "YYYY-MM-DD HH:MM:SS" in Hong Kong time. */
export function formatHkt(iso: string): string {
  return _format(iso, "Asia/Hong_Kong");
}

/** ISO-8601 UTC → "YYYY-MM-DD HH:MM:SS" in UTC (drawer secondary line). */
export function formatUtc(iso: string): string {
  return _format(iso, "UTC");
}

/** ISO-8601 UTC → "YYYY-MM-DD HH:MM" in Hong Kong time (most screens). */
export function formatHktDateTime(iso: string): string {
  return formatHkt(iso).slice(0, 16);
}

/** ISO-8601 UTC → its Hong Kong calendar day, "YYYY-MM-DD". */
export function formatHktDate(iso: string): string {
  return formatHkt(iso).slice(0, 10);
}

/** Today's Hong Kong calendar day, "YYYY-MM-DD" — the default for date
 *  pickers (the browser's own date is a day behind before 08:00 HKT when it
 *  runs on UTC, and differs again elsewhere). */
export function hktToday(now: Date = new Date()): string {
  return formatHktDate(now.toISOString());
}

/** A calendar day "YYYY-MM-DD" moved by `n` whole days (zone-free). */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** The instant `days` × 24 h before `now` — the Log's rolling presets. */
export function daysAgoIso(days: number, now: number = Date.now()): string {
  return new Date(now - days * 24 * 3600 * 1000).toISOString();
}

/** Whether an instant is exactly 00:00:00 in Hong Kong (a picked day's start,
 *  as opposed to a rolling preset's). */
export function isHktMidnight(iso: string): boolean {
  return formatHkt(iso).endsWith(" 00:00:00");
}

// HKT is a fixed +08:00 offset (no DST), so a date-picker value (HKT calendar
// day) maps to a UTC instant deterministically. Used to convert the audit
// filter date pickers to the UTC range the API expects.

/** HKT calendar day "YYYY-MM-DD" → ISO-8601 UTC at 00:00:00 HKT. */
export function hktDayStartUtc(day: string): string {
  return new Date(`${day}T00:00:00+08:00`).toISOString();
}

/** HKT calendar day "YYYY-MM-DD" → ISO-8601 UTC at 23:59:59.999 HKT (inclusive end). */
export function hktDayEndUtc(day: string): string {
  return new Date(`${day}T23:59:59.999+08:00`).toISOString();
}

/** A Log `from` / `to` as it sits in the URL → ISO-8601 UTC, read the way the
 *  API reads it (`parse_hkt_bound`): a bare day (`YYYY-MM-DD` or `YYYYMMDD`)
 *  is that HKT day's start, or its end when `end`; a time without an offset
 *  is HKT. Anything else (the UI's own `Z` instants) passes through.
 *  `new Date()` would read a bare day as UTC and a bare time as local. */
export function hktBoundToUtc(text: string, end = false): string {
  const day = /^(\d{4})-?(\d{2})-?(\d{2})$/.exec(text);
  if (day) {
    const ymd = `${day[1]}-${day[2]}-${day[3]}`;
    return end ? hktDayEndUtc(ymd) : hktDayStartUtc(ymd);
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(text)) {
    return new Date(`${text}+08:00`).toISOString();
  }
  return text;
}
